/**
 * Text Background Utility
 *
 * Masks images over text using background-clip technique.
 * Creates dramatic image-filled text effects.
 *
 * Usage:
 * <h1 data-text-background="https://example.com/image.jpg">Amazing Text</h1>
 *
 * Attributes:
 * - data-text-background: Image URL to mask over text (required)
 * - data-text-bg-size: background-size value (default: "cover")
 * - data-text-bg-position: background-position value (default: "center")
 * - data-text-bg-attachment: background-attachment (default: "scroll")
 */

class TextBackground {
	constructor() {
		this.elements = [];
	}

	/**
	 * Setup text background for an element
	 * @param {HTMLElement} element - The element with text to mask
	 */
	setupElement(element) {
		const imageUrl = element.dataset.textBackground;

		if (!imageUrl) {
			return;
		}

		// Get optional configuration
		const bgSize = element.dataset.textBgSize || 'cover';
		const bgPosition = element.dataset.textBgPosition || 'center';
		const bgAttachment = element.dataset.textBgAttachment || 'scroll';

		// Add the text-background class for CSS styling
		element.classList.add('has-text-background');

		// Apply background image and properties
		element.style.backgroundImage = `url(${imageUrl})`;
		element.style.backgroundSize = bgSize;
		element.style.backgroundPosition = bgPosition;
		element.style.backgroundAttachment = bgAttachment;

		this.elements.push(element);
	}

	/**
	 * Initialize all text background elements
	 */
	init() {
		const elements = document.querySelectorAll('[data-text-background]');

		elements.forEach((element) => {
			this.setupElement(element);
		});
	}

	/**
	 * Refresh/reinitialize all elements
	 */
	refresh() {
		// Clear existing
		this.elements = [];

		// Reinitialize
		this.init();
	}

	/**
	 * Destroy all text backgrounds
	 */
	destroy() {
		this.elements.forEach((element) => {
			element.classList.remove('has-text-background');
			element.style.backgroundImage = '';
			element.style.backgroundSize = '';
			element.style.backgroundPosition = '';
			element.style.backgroundAttachment = '';
		});

		this.elements = [];
	}
}

// Create and export instance
const textBackground = new TextBackground();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => textBackground.init());
} else {
	textBackground.init();
}

export default textBackground;
export { TextBackground };
